import { ref, type Ref } from 'vue';

import {
  downloadBlobFile,
  formatDateForApi,
  handleApiError,
  useCustomToast,
  useDocumentSearchTypeStore,
  type Pagination,
  type SearchFilter,
} from '@/shared/lib';

import { validityHistoryApi } from '../api';
import { VALIDITY_HISTORY_EXPORT_GENERIC_RETRY_MESSAGE } from '../config/messages';

type UseValidityHistoryExportParams = {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filter: Ref<SearchFilter>;
  /** Export endpoint of the active tab. Without it, the Documentos one is used. */
  exportUrl?: Ref<string>;
  metadata?: Ref<Pagination | null | undefined>;
};

type ValidityHistoryExportRequest = {
  term?: string;
  searchType?: 'name' | 'prefix';
  sort?: string;
  order?: string;
  documentType?: 'DOCUMENT' | 'REGISTRATION';
  status?: string;
  elaboratedBy?: string;
  approvedBy?: string;
  verifiedBy?: string;
  elaborationDate?: string;
  approvalDate?: string;
  verificationDate?: string;
  effectiveDate?: string;
  revision?: number;
};

function formatDateValue(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  if (value instanceof Date) {
    return formatDateForApi(value) ?? undefined;
  }

  const normalized = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  return normalized.includes('T') ? normalized.slice(0, 10) : normalized;
}

function normalizeRevisionValue(value: unknown): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export const useValidityHistoryExport = ({
  searchTerm,
  sort,
  order,
  filter,
  exportUrl,
}: UseValidityHistoryExportParams) => {
  const toast = useCustomToast();
  const { searchType } = useDocumentSearchTypeStore();
  const isExporting = ref(false);
  const exportMessage = ref('');

  const buildExportParams = (): ValidityHistoryExportRequest => {
    const currentFilter = filter.value;

    return {
      term: searchTerm.value.trim() || undefined,
      searchType: searchType.value,
      sort: sort.value || undefined,
      order: order.value || undefined,
      documentType: currentFilter.documentType as 'DOCUMENT' | 'REGISTRATION' | undefined,
      status: currentFilter.status?.toString() || undefined,
      elaboratedBy: currentFilter.elaboratedBy?.toString() || undefined,
      approvedBy: currentFilter.approvedBy?.toString() || undefined,
      verifiedBy: currentFilter.verifiedBy?.toString() || undefined,
      elaborationDate: formatDateValue(currentFilter.elaborationDate),
      approvalDate: formatDateValue(currentFilter.approvalDate),
      verificationDate: formatDateValue(currentFilter.verificationDate),
      effectiveDate: formatDateValue(currentFilter.effectiveDate),
      revision: normalizeRevisionValue(currentFilter.revision),
    };
  };

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;
    exportMessage.value = 'Exportação em andamento. Por favor, aguarde.';

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const { blob, exportedCount, filename } =
        await validityHistoryApi.exportValidityHistoryExcel(buildExportParams(), exportUrl?.value);

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1 ? '1 documento exportado' : `${exportedCount} documentos exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, VALIDITY_HISTORY_EXPORT_GENERIC_RETRY_MESSAGE);
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      isExporting.value = false;
      exportMessage.value = '';
    }
  };

  return {
    handleExportToExcel,
    isExporting,
    exportMessage,
  };
};
